import { useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronDown, CircleX, Filter, ListRestart } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDebouncedCallback } from 'use-debounce';
import { z } from 'zod/v4';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';

import { Route as AdminRoute } from '@/routes/admin';

import { cn } from '@/utils';

import { topicStatuses, trendCategories } from '@/config';

const recentHoursOptions = [
  { label: 'Last 6 hours', value: 6 },
  { label: 'Last 12 hours', value: 12 },
  { label: 'Last 24 hours', value: 24 },
  { label: 'Last 3 days', value: 3 * 24, default: true },
  { label: 'Last 7 days', value: 7 * 24 },
  { label: 'Last 14 days', value: 14 * 24 },
  { label: 'Last 30 days', value: 30 * 24 },
  { label: 'Last 60 days', value: 60 * 24 },
  { label: 'Last 90 days', value: 90 * 24 },
  { label: 'Last 120 days', value: 120 * 24 },
  { label: 'Last 180 days', value: 180 * 24 },
];

const postRangePresets = [
  { label: 'All', value: [0, Number.MAX_SAFE_INTEGER] },
  { label: '<=250', value: [0, 250] },
  { label: '<=500', value: [0, 500] },
  { label: '<=1,000', value: [0, 1000] },
  { label: '<=2,500', value: [0, 2500] },
  { label: '<=5,000', value: [0, 5000] },
  { label: '1,000-2,500', value: [1000, 2500] },
  { label: '2,500-5,000', value: [2500, 5000] },
  { label: '5,000-10,000', value: [5000, 10000] },
  { label: '>=10000', value: [10000, Number.MAX_SAFE_INTEGER] },
];

const maxAveragePostsFilter = 50_000;

const defaultRecentHours = recentHoursOptions.find((range) => range.default)?.value ?? 24;

export const defaultSearch = {
  search: '',
  recentHours: defaultRecentHours,
  trendCategory: 'all',
  summaryStatus: 'all',
  sortColumn: 'createdAt',
  sortDirection: 'desc' as 'asc' | 'desc' | '',
  averagePosts: [0, Number.MAX_SAFE_INTEGER],
  isTrending: false,
  isArchived: false,
};

export const searchSchema = z.object({
  search: z.string().optional(),
  recentHours: z.number().default(defaultSearch.recentHours),
  trendCategory: z.string().default(defaultSearch.trendCategory),
  summaryStatus: z.string().default(defaultSearch.summaryStatus),
  sortColumn: z.string().default(defaultSearch.sortColumn),
  sortDirection: z.enum(['asc', 'desc', '']).default(defaultSearch.sortDirection),
  averagePosts: z.array(z.number()).length(2).default(defaultSearch.averagePosts),
  isTrending: z.boolean().default(false),
  isArchived: z.boolean().default(false),
});
export type SearchParams = z.infer<typeof searchSchema>;

export default function RecentTrendsTableToolbar() {
  const search = useSearch({ from: AdminRoute.id });
  const navigate = useNavigate({ from: AdminRoute.id });
  const [inputValue, setInputValue] = useState(search.search ?? '');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [minAveragePosts, setMinAveragePosts] = useState<number>(search.averagePosts[0]);
  const [maxAveragePosts, setMaxAveragePosts] = useState<number>(search.averagePosts[1]);

  useHotkeys('ctrl+f', () => {
    searchInputRef.current?.focus();
  });

  useHotkeys('ctrl+r', () => {
    handleResetFilters();
  });

  const debouncedNavigate = useDebouncedCallback((value: string) => {
    navigate({
      search: (prev) => {
        if (!value) {
          const { search: _, ...rest } = prev;

          return rest;
        }

        return { ...prev, search: value };
      },
    });
  }, 400);

  const setSearch = (searchParams: Partial<SearchParams>) => {
    navigate({
      search: (prev) => ({ ...prev, ...searchParams }),
      resetScroll: false,
    });
  };

  useEffect(() => {
    setInputValue(search.search ?? '');
  }, [search.search]);

  const handleClearSearch = () => {
    setInputValue('');
    debouncedNavigate('');
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setInputValue(newValue);
    debouncedNavigate(newValue);
  };

  const handleResetFilters = () => {
    navigate({ search: defaultSearch });
  };

  const handleAveragePostsChange = (value: number[], updateState = false) => {
    setSearch({ averagePosts: value });

    if (updateState) {
      setMinAveragePosts(value[0]);
      setMaxAveragePosts(value[1]);
    }
  };

  let averagePostsLabel = 'Avg. Posts: ';

  if (minAveragePosts === 0 && maxAveragePosts === Number.MAX_SAFE_INTEGER) {
    averagePostsLabel += 'All';
  } else if (minAveragePosts === 0) {
    averagePostsLabel += `<= ${maxAveragePosts.toLocaleString()}`;
  } else if (maxAveragePosts === Number.MAX_SAFE_INTEGER) {
    averagePostsLabel += `>= ${minAveragePosts.toLocaleString()}`;
  } else {
    averagePostsLabel += `${minAveragePosts.toLocaleString()} - ${maxAveragePosts.toLocaleString()}`;
  }

  const miscFilters = search.isTrending || search.isArchived;

  return (
    <div className="my-4 flex flex-col items-center justify-between gap-4 md:flex-row">
      <div className="relative w-full md:w-80">
        <Input
          ref={searchInputRef}
          type="text"
          className="w-full pr-8"
          placeholder="Search"
          value={inputValue}
          onChange={handleSearch}
        />
        {inputValue && (
          <CircleX
            className="-translate-y-1/2 absolute top-1/2 right-2 size-4 cursor-pointer text-gray-400"
            onClick={handleClearSearch}
          />
        )}
      </div>
      <div className="flex w-full flex-row flex-wrap items-center justify-end gap-2">
        <Select
          value={search.recentHours.toString()}
          onValueChange={(value) => {
            setSearch({ recentHours: Number(value) });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select date range" />
          </SelectTrigger>
          <SelectContent>
            {recentHoursOptions.map((range) => (
              <SelectItem key={range.value} value={range.value.toString()}>
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={search.trendCategory}
          onValueChange={(value) => {
            setSearch({ trendCategory: value });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select trend category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.keys(trendCategories).map((category: string) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger
            asChild
            className="data-[state=open]:min-w-60 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground"
          >
            <Button variant="outline" className="font-normal">
              {averagePostsLabel}
              <ChevronDown className="size-4 opacity-25" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] sm:w-[450px]">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row flex-wrap items-center justify-between gap-2">
                {postRangePresets.map((preset) => (
                  <Badge
                    key={preset.value.toString()}
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => {
                      handleAveragePostsChange(preset.value, true);
                    }}
                  >
                    {preset.label}
                  </Badge>
                ))}
              </div>

              <div className="flex flex-row items-center justify-between gap-2">
                <div className="text-muted-foreground text-sm">
                  {minAveragePosts.toLocaleString()}
                </div>
                <div className="text-muted-foreground text-sm">
                  {maxAveragePosts > maxAveragePostsFilter
                    ? `> ${maxAveragePostsFilter.toLocaleString()}`
                    : maxAveragePosts.toLocaleString()}
                </div>
              </div>
              <Slider
                defaultValue={[search.averagePosts[0], search.averagePosts[1]]}
                value={[minAveragePosts, maxAveragePosts]}
                onValueChange={(value) => {
                  setMinAveragePosts(value[0]);
                  setMaxAveragePosts(
                    value[1] > maxAveragePostsFilter ? Number.MAX_SAFE_INTEGER : value[1]
                  );
                }}
                onValueCommit={(value) => {
                  handleAveragePostsChange(value);
                }}
                min={0}
                max={maxAveragePostsFilter + 1000}
                step={minAveragePosts < 5000 ? 250 : minAveragePosts < 25000 ? 1000 : 5000}
              />
            </div>
          </PopoverContent>
        </Popover>

        <Select
          value={search.summaryStatus}
          onValueChange={(value) => {
            setSearch({ summaryStatus: value });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select summary status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {topicStatuses.map((status: string) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger
            asChild
            className="data-[state=open]:bg-accent data-[state=open]:text-accent-foreground"
          >
            <Button variant="outline" className="font-normal">
              <Filter className={cn('size-4', miscFilters && 'fill-current')} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-40" align="end">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row items-center gap-2">
                <Checkbox
                  id="trending"
                  checked={search.isTrending}
                  onCheckedChange={(checked) => setSearch({ isTrending: !!checked })}
                />
                <Label htmlFor="trending">Trending</Label>
              </div>
              <div className="flex flex-row items-center gap-2">
                <Checkbox
                  id="archived"
                  checked={search.isArchived}
                  onCheckedChange={(checked) => setSearch({ isArchived: !!checked })}
                />
                <Label htmlFor="archived">Archived</Label>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button variant="outline" size="icon" onClick={handleResetFilters}>
          <ListRestart className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
